import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";

import { showError } from "redux/actions/ui";
import { TAccountingReference } from "redux/reducers/models/facility";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import DataTable from "../../../../../components/dataTable/DataTable";
import Input from "components/form/input/Input";

import "../facility/facility.scss";

type AccountFilters = {
  search: string;
};

export default function AccountingReferences() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [accountReferences, setAccountReferences] = useState<TAccountingReference[]>(undefined);

  const [filterState, setFilterState] = useState<AccountFilters>({
    search: "",
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccountingReferences(source.token);
    return () => source.cancel();
  }, []);

  async function loadAccountingReferences(token?: CancelToken) {
    if (accountReferences !== undefined) {
      setAccountReferences(undefined);
    }

    const res = await GetAccountingReference(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading accounting references")));
    }

    setAccountReferences(res.status !== StatusCode.OK ? [] : res.data);
  }

  const primaryAction = {
    content: t("New Accounting Reference"),
    action: () => history.push("/admin/settings/accounting_references/new"),
  };

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setFilterState(prevState => ({ ...prevState, [id]: value }));
  }

  async function searchAccountingReferences(token?: CancelToken) {
    const accountReferencesRes = await GetAccountingReference(
      {
        search: filterState.search,
      },
      token ? false : true,
    );

    console.log(accountReferencesRes);

    if (accountReferencesRes.status !== StatusCode.OK) {
      return [];
    }

    setAccountReferences(accountReferencesRes.data);

    return accountReferencesRes.data;
  }

  useEffect(() => {
    let mounted = true;
    let timeoutId: number = null;
    const source = axios.CancelToken.source();
    const search = () => {
      timeoutId = window.setTimeout(() => {
        void (async () => {
          if (filterState.search !== null) {
            try {
              const res = await searchAccountingReferences(source.token);
              if (mounted) {
                setAccountReferences(res);
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          }
        })();
      }, 1000);
    };

    search();
    return () => {
      source.cancel();
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [filterState.search]);

  return (
    <Page
      title={t("Accounting References")}
      subtitle={t("Manage acccounting references")}
      narrow
      primaryAction={primaryAction}
    >
      <div className="flex-grow mb-4 pt-4">
        <Input
          value={filterState.search}
          label={t("Search Accounts")}
          id="search"
          onChange={handleInputChange}
          placeholder={t("Search...")}
          autoComplete="off"
        />
      </div>

      <DataTable
        columns={[
          { label: t("Title"), width: "50%" },
          { label: t("Reference Number"), width: "50%" },
        ]}
        loading={accountReferences === undefined}
      >
        {accountReferences?.map(reference => (
          <tr
            key={reference.id}
            onClick={() => history.push("/admin/settings/accounting_references/" + String(reference.id))}
            className="clickable"
          >
            <td>{reference.title}</td>
            <td>{reference.reference_number}</td>
          </tr>
        ))}
      </DataTable>
    </Page>
  );
}
